if (!customElements.get("complementary-product")) {
  customElements.define(
    "complementary-product",
    class ComplementaryProduct extends HTMLElement {
      connectedCallback() {
        if (this.dataset.bound === "true") return;
        this.dataset.bound = "true";

        this.sectionId = this.dataset.sectionId;
        this.productFormId = this.dataset.productFormId;

        this.bindViewAll();
        this.bindDrawer();
        this.bindPriceUpdates();
        this.bindCardActions();
        this.bindAddToCart();
      }

      bindViewAll() {
        this.viewAll = this.querySelector("[data-complementary-view-all]");
        if (!this.viewAll) return;

        this.viewAll.addEventListener("click", (event) => {
          event.preventDefault();
          this.openDrawer();
        });
      }

      bindDrawer() {
        this.drawer = this.querySelector(".complementary-product__drawer");
        if (!this.drawer) return;

        this.drawer.querySelectorAll("[data-drawer-close]").forEach((el) => {
          el.addEventListener("click", () => this.closeDrawer());
        });

        this.onDrawerKeyup = (event) => {
          if (event.key === "Escape") this.closeDrawer();
        };
      }

      openDrawer() {
        if (!this.drawer) return;

        this.drawer.hidden = false;
        requestAnimationFrame(() => {
          this.drawer.classList.add("is-open");
        });
        if (this.viewAll) this.viewAll.setAttribute("aria-expanded", "true");
        document.body.classList.add("overflow-hidden");
        document.addEventListener("keyup", this.onDrawerKeyup);

        const closeButton = this.drawer.querySelector(".complementary-product__drawer-close");
        if (closeButton) closeButton.focus();
      }

      closeDrawer() {
        if (!this.drawer || !this.drawer.classList.contains("is-open")) return;

        this.drawer.classList.remove("is-open");
        if (this.viewAll) this.viewAll.setAttribute("aria-expanded", "false");
        document.body.classList.remove("overflow-hidden");
        document.removeEventListener("keyup", this.onDrawerKeyup);
        this.closeAllActions();

        setTimeout(() => {
          if (!this.drawer.classList.contains("is-open")) this.drawer.hidden = true;
          if (this.viewAll) this.viewAll.focus();
        }, 300);
      }

      bindPriceUpdates() {
        this.addEventListener("change", (event) => {
          if (!event.target.classList.contains("complementary-product-card__input")) return;
          this.syncSelection(event.target);
          this.updateDisplayedPrice();
        });

        const priceContainer = document.getElementById(`price-${this.sectionId}`);
        if (priceContainer) {
          this.priceObserver = new MutationObserver(() => this.updateDisplayedPrice());
          this.priceObserver.observe(priceContainer, { childList: true });
        }
      }

      bindCardActions() {
        this.addEventListener("click", (event) => {
          const plusButton = event.target.closest("[data-complementary-plus]");
          const cartButton = event.target.closest("[data-complementary-cart]");
          const card = event.target.closest(".complementary-product-card");
          if (!card || !this.contains(card)) return;

          if (event.target.closest("[data-complementary-view-cart]")) return;
          if (event.target.closest(".complementary-product-card__title")) return;
          if (card.classList.contains("is-added")) return;

          if (plusButton) {
            event.preventDefault();
            if (!card.classList.contains("is-added")) this.toggleAddon(card);
            return;
          }

          if (cartButton) {
            event.preventDefault();
            this.addItemToCart(card, cartButton);
            return;
          }

          this.toggleActions(card);
        });

        this.onDocumentClick = (event) => {
          if (this.contains(event.target)) return;
          this.closeAllActions();
        };
        document.addEventListener("click", this.onDocumentClick);
      }

      toggleActions(card) {
        const isOpen = card.classList.contains("is-actions-open");
        this.closeAllActions();
        if (!isOpen) card.classList.add("is-actions-open");
      }

      closeAllActions() {
        this.querySelectorAll(".complementary-product-card.is-actions-open").forEach((card) => {
          if (card.classList.contains("is-added")) return;
          card.classList.remove("is-actions-open");
        });
      }

      toggleAddon(card) {
        if (card.classList.contains("is-added")) return;
        const input = card.querySelector(".complementary-product-card__input");
        if (!input) return;

        input.checked = !input.checked;
        this.syncSelection(input);
        this.updateDisplayedPrice();
        this.syncPlusLabels(input);
      }

      syncPlusLabels(sourceInput) {
        this.querySelectorAll(".complementary-product-card__input").forEach((input) => {
          if (input.value !== sourceInput.value) return;
          const button = input.closest(".complementary-product-card")?.querySelector("[data-complementary-plus]");
          if (!button) return;
          button.setAttribute(
            "aria-label",
            input.checked ? button.dataset.deselectLabel : button.dataset.selectLabel
          );
        });
      }

      syncSelection(sourceInput) {
        this.querySelectorAll(".complementary-product-card__input").forEach((input) => {
          if (input === sourceInput || input.value !== sourceInput.value) return;
          input.checked = sourceInput.checked;
        });
      }

      bindAddToCart() {
        const form = document.getElementById(this.productFormId);
        if (!form) return;

        form.addEventListener("submit", this.onFormSubmit.bind(this), true);
      }

      getSelectedItems() {
        const seen = {};
        const items = [];

        this.querySelectorAll(".complementary-product-card__input:checked").forEach((input) => {
          const id = Number(input.value);
          if (seen[id]) return;
          seen[id] = true;
          items.push({
            id: id,
            price: Number(input.dataset.price) || 0,
          });
        });

        return items;
      }

      updateDisplayedPrice() {
        const priceEl = document.querySelector(`#price-${this.sectionId} .price-regular`);
        if (!priceEl) return;

        const basePrice = Number(priceEl.dataset.variantPrice) || 0;
        const addOnPrice = this.getSelectedItems().reduce((sum, item) => sum + item.price, 0);
        const totalPrice = basePrice + addOnPrice;
        priceEl.textContent = this.formatMoney(totalPrice);
        this.updateAfterpayPrice(totalPrice);
      }

      updateAfterpayPrice(totalCents) {
        const afterpayEl = document.querySelector(`#price-${this.sectionId} .js-afterpay-price`);
        if (!afterpayEl) return;

        const prefixMatch = afterpayEl.textContent.match(/^(\d+)\s*x\s*/i);
        const installments = prefixMatch ? Number(prefixMatch[1]) : 4;
        const prefix = prefixMatch ? prefixMatch[0] : "4 x ";
        const installmentCents = Math.floor(totalCents / installments);
        afterpayEl.textContent = prefix + this.formatMoney(installmentCents, true);
      }

      formatMoney(cents, withCurrency) {
        let format = withCurrency
          ? window.shopMoneyWithCurrencyFormat || window.shopMoneyFormat || "${{amount}}"
          : window.shopMoneyFormat || "${{amount}}";
        const value = (Number(cents) / 100).toFixed(2);
        const withThousands = value.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        format = format.replace(/\{\{\s*currency\s*\}\}/g, window.shopCurrency || "");
        return format.replace(/\{\{\s*\w+\s*\}\}/, withThousands);
      }

      addItemToCart(card, button) {
        if (!button || button.disabled || button.classList.contains("is-loading")) return;
        if (card.classList.contains("is-added")) return;

        const input = card.querySelector(".complementary-product-card__input");
        const variantId = Number(input && input.value);
        if (!variantId) return;

        this.setCartLoading(variantId, true);

        const sections = ["cart-icon-bubble"];
        if (document.getElementById("cart-live-region-text")) {
          sections.push("cart-live-region-text");
        }
        if (document.getElementById("CartDrawer")) {
          sections.push("cart-drawer");
        }

        fetch(window.routes.cart_add_url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: sections.join(","),
            sections_url: window.location.pathname,
          }),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              throw new Error(response.description || response.message || "Cart error");
            }

            if (input && input.checked) {
              input.checked = false;
              this.syncSelection(input);
              this.updateDisplayedPrice();
              this.syncPlusLabels(input);
            }

            this.renderCartSections(response.sections);
            this.publishCartUpdate(response, variantId);
            this.setAddedState(variantId);
          })
          .catch((error) => {
            console.error(error);
            this.setCartLoading(variantId, false);
          });
      }

      setAddedState(variantId) {
        this.querySelectorAll(`[data-complementary-cart][data-variant-id="${variantId}"]`).forEach((button) => {
          const addedCard = button.closest(".complementary-product-card");
          if (!addedCard) return;
          addedCard.classList.add("is-added");
          addedCard.classList.add("is-actions-open");
        });
        this.setCartLoading(variantId, false);
      }

      renderCartSections(sections) {
        if (!sections) return;

        Object.entries(sections).forEach(([sectionId, sectionHTML]) => {
          const target = document.getElementById(sectionId);
          if (!target || !sectionHTML) return;

          const html = new DOMParser().parseFromString(sectionHTML, "text/html");
          const source = html.querySelector(".shopify-section") || html.body;

          if (sectionId === "cart-icon-bubble") {
            target.innerHTML = source.querySelector("#cart-icon-bubble")
              ? source.querySelector("#cart-icon-bubble").innerHTML
              : source.innerHTML;
            return;
          }

          if (sectionId === "cart-drawer") {
            const drawer = document.getElementById("CartDrawer");
            const nextDrawer = source.querySelector("#CartDrawer");
            if (drawer && nextDrawer) drawer.innerHTML = nextDrawer.innerHTML;
            return;
          }

          target.innerHTML = source.innerHTML;
        });
      }

      publishCartUpdate(cartData, variantId) {
        if (typeof publish !== "function" || typeof PUB_SUB_EVENTS === "undefined") return;

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "complementary-product",
          cartData: cartData,
          variantId: variantId,
        });
      }

      setCartLoading(variantId, isLoading) {
        this.querySelectorAll(`[data-complementary-cart][data-variant-id="${variantId}"]`).forEach((button) => {
          const spinner = button.querySelector(".complementary-product-card__action-spinner");
          button.classList.toggle("is-loading", isLoading);
          button.toggleAttribute("aria-disabled", isLoading);
          if (spinner) {
            spinner.classList.toggle("hidden", !isLoading);
            spinner.setAttribute("aria-hidden", isLoading ? "false" : "true");
          }
        });
      }

      onFormSubmit(event) {
        const selectedItems = this.getSelectedItems();
        if (!selectedItems.length) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const form = event.currentTarget;
        const productForm = form.closest("product-form");
        const submitButton = form.querySelector('[type="submit"]');
        if (submitButton && submitButton.classList.contains("loading")) return;

        const parentId = Number(form.querySelector('input[name="id"]').value);
        const quantityInput = form.querySelector('input[name="quantity"]');
        const quantity = quantityInput ? Number(quantityInput.value) || 1 : 1;

        const items = [
          { id: parentId, quantity: quantity },
        ].concat(
          selectedItems.map((item) => ({
            id: item.id,
            quantity: quantity,
            parent_id: parentId,
          }))
        );

        this.toggleLoading(productForm, submitButton, true);

        fetch(window.routes.cart_add_url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ items: items }),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.showFormError(productForm, response.description);
              return;
            }

            const url = window.location.href;
            const join = url.indexOf("?") > -1 ? "&" : "?";
            window.location.href = url + join + "done#success";
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.toggleLoading(productForm, submitButton, false);
          });
      }

      toggleLoading(productForm, submitButton, isLoading) {
        if (!submitButton) return;

        if (isLoading) {
          submitButton.setAttribute("aria-disabled", "true");
          submitButton.classList.add("loading");
        } else {
          submitButton.classList.remove("loading");
          submitButton.removeAttribute("aria-disabled");
        }

        if (!productForm) return;
        const spinner = productForm.querySelector(".loading-overlay__spinner");
        if (!spinner) return;
        spinner.classList.toggle("hidden", !isLoading);
      }

      showFormError(productForm, message) {
        if (productForm && typeof productForm.handleErrorMessage === "function") {
          productForm.handleErrorMessage(message);
          return;
        }
        console.error(message);
      }
    }
  );
}

document.addEventListener("DOMContentLoaded", function () {
  const ua = window.navigator.userAgent;
  if (ua.indexOf("MSIE ") < 0 && ua.indexOf("Trident/") < 0) return;

  document.querySelectorAll("[data-product-container]").forEach((container) => {
    const sectionId = container.dataset.sectionId;
    const formId = container.dataset.productFormId;
    if (!sectionId || !formId) return;

    const hiddenInput = document.querySelector(`#${formId} input[name="id"]`);
    const variantSwitcher =
      document.querySelector(`variant-radios[data-section="${sectionId}"]`) ||
      document.querySelector(`variant-selects[data-section="${sectionId}"]`);
    const noScriptWrapper = document.querySelector(`.product-form__noscript-wrapper-${sectionId}`);

    if (!hiddenInput || !variantSwitcher || !noScriptWrapper) return;

    const noScriptInputWrapper = document.createElement("div");
    noScriptInputWrapper.innerHTML = noScriptWrapper.textContent;
    variantSwitcher.outerHTML = noScriptInputWrapper.outerHTML;

    const variantsSelect = document.querySelector(`#Variants-${sectionId}`);
    if (!variantsSelect) return;

    variantsSelect.addEventListener("change", function (event) {
      hiddenInput.value = event.currentTarget.value;
    });
  });
});
